import { useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';
import { useToast } from '../components/Toast';

export default function Admins() {
  const toast = useToast();
  const [tenantId, setTenantId] = useState<string>('');
  const [groups, setGroups] = useState<any[]>([]);
  const [adminGroups, setAdminGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [m365Groups, setM365Groups] = useState<any[]>([]);
  const [loadingM365Groups, setLoadingM365Groups] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [assigningGroup, setAssigningGroup] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [processingUsers, setProcessingUsers] = useState(false);
  const [dbUsers, setDbUsers] = useState<any[]>([]);
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [showGroupMembersModal, setShowGroupMembersModal] = useState(false);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState<any[]>([]);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [loadingGroupMembers, setLoadingGroupMembers] = useState(false);

  // Get current user's tenant on mount
  useEffect(() => {
    apiClient.get('/api/auth/me')
      .then((data) => {
        if (data.success) {
          setTenantId(data.data.tenantId);
        }
      })
      .catch((error) => console.error('Failed to get user info:', error));
  }, []);

  // Load admin groups and users when tenant changes
  useEffect(() => {
    if (tenantId) {
      loadAdminGroups();
      loadAdminUsers();
    }
  }, [tenantId]);

  const loadM365Groups = async () => {
    if (!tenantId) return;
    setLoadingM365Groups(true);
    try {
      // Fetch available groups from Microsoft Graph
      const data = await apiClient.get(`/api/groups/available/${tenantId}`);
      if (data.success) {
        setM365Groups(data.data);
      }
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
    setLoadingM365Groups(false);
  };

  const assignGroupAsAdmin = async (graphGroupId: string, groupName: string) => {
    setAssigningGroup(graphGroupId);
    try {
      // First sync the group
      const syncData = await apiClient.post('/api/groups/sync-single', {
        tenantId,
        graphGroupId,
      });
      
      if (!syncData.success) {
        toast.error(`Failed to sync group: ${syncData.error}`);
        return;
      }

      // Get the synced group ID from database
      const groupsData = await apiClient.get(`/api/groups/${tenantId}`);
      const syncedGroup = groupsData.data.find((g: any) => g.graphGroupId === graphGroupId);

      if (!syncedGroup) {
        toast.error('Failed to find synced group');
        return;
      }

      // Assign as admin group
      const assignData = await apiClient.post('/api/admins/assign', {
        groupId: syncedGroup.id,
      });

      if (assignData.success) {
        toast.success(`${groupName} assigned as admin group!`);
        await loadAdminGroups();
        // Keep modal open and reload to show updated state
        await loadM365Groups();
      } else {
        toast.error(`Assignment failed: ${assignData.error}`);
      }
    } catch (error: any) {
      toast.error(`Failed: ${error.message}`);
    }
    setAssigningGroup(null);
  };

  const loadUsers = async () => {
    if (!tenantId) return;
    setLoadingUsers(true);
    try {
      // Fetch users directly from Microsoft Graph
      const data = await apiClient.get(`/api/users/available/${tenantId}`);
      if (data.success) {
        setUsers(data.data);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
    setLoadingUsers(false);
  };

  const loadAdminGroups = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/api/admins/groups/${tenantId}`);
      if (data.success) {
        setAdminGroups(data.data);
      }
    } catch (error) {
      console.error('Failed to load admin groups:', error);
    }
    setLoading(false);
  };

  const loadAdminUsers = async () => {
    if (!tenantId) return;
    try {
      const data = await apiClient.get(`/api/users/${tenantId}?limit=1000`);
      if (data.success) {
        // Filter only admin users
        setAdminUsers(data.data.filter((user: any) => user.isAdmin));
      }
    } catch (error) {
      console.error('Failed to load admin users:', error);
    }
  };

  const handleAssign = async (groupId: string) => {
    try {
      const data = await apiClient.post('/api/admins/assign', { groupId });
      if (data.success) {
        loadAdminGroups();
      } else {
        toast.error(`Assignment failed: ${data.error}`);
      }
    } catch (error: any) {
      toast.error(`Assignment failed: ${error.message}`);
    }
  };

  const handleUnassign = async (groupId: string) => {
    try{
      const data = await apiClient.post('/api/admins/unassign', { groupId });
      if (data.success) {
        loadAdminGroups();
      }
    } catch (error: any) {
      toast.error(`Unassignment failed: ${error.message}`);
    }
  };

  const toggleUserSelection = (graphUserId: string) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(graphUserId)) {
      newSelected.delete(graphUserId);
    } else {
      newSelected.add(graphUserId);
    }
    setSelectedUserIds(newSelected);
  };

  const processSelectedUsers = async () => {
    if (selectedUserIds.size === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setProcessingUsers(true);
    const errors: string[] = [];

    for (const graphUserId of selectedUserIds) {
      try {
        const m365User = users.find(u => u.graphUserId === graphUserId);
        const dbUser = dbUsers.find(u => u.graphUserId === graphUserId);
        const currentIsAdmin = dbUser?.isAdmin || false;
        const userName = m365User?.displayName || graphUserId;

        if (!currentIsAdmin) {
          // User should become admin - sync and add
          const data = await apiClient.post('/api/admins/add-user', {
            graphUserId,
          });
          if (!data.success) {
            errors.push(`${userName}: ${data.error}`);
          }
        } else {
          // User should be removed from admin
          if (!dbUser) {
            errors.push(`${userName}: User not found in database`);
            continue;
          }

          const data = await apiClient.post('/api/admins/toggle-user', {
            userId: dbUser.id,
            isAdmin: false,
          });
          if (!data.success) {
            errors.push(`${userName}: ${data.error}`);
          }
        }
      } catch (error: any) {
        const m365User = users.find(u => u.id === graphUserId);
        const userName = m365User?.displayName || graphUserId;
        errors.push(`${userName}: ${error.message}`);
      }
    }

    if (errors.length > 0) {
      toast.error(`Some operations failed:\n${errors.join('\n')}`);
    }

    await loadDbUsers();
    await loadAdminUsers();
    // Keep modal open and reload to show updated state
    await loadUsers();
    setSelectedUserIds(new Set());
    setProcessingUsers(false);
  };

  const loadDbUsers = async () => {
    if (!tenantId) return;
    try {
      const data = await apiClient.get(`/api/users/${tenantId}?limit=1000`);
      if (data.success) {
        setDbUsers(data.data);
      }
    } catch (error) {
      console.error('Failed to load db users:', error);
    }
  };

  const filteredUsers = userSearch
    ? users.filter(
        (user) =>
          user.displayName?.toLowerCase().includes(userSearch.toLowerCase()) ||
          user.email?.toLowerCase().includes(userSearch.toLowerCase())
      )
    : users;

  // When modal opens, load both M365 users and DB users
  const openUserModal = () => {
    setShowUserModal(true);
    loadUsers();
    loadDbUsers();
  };

  const handleViewGroupMembers = async (groupId: string, groupName: string) => {
    setSelectedGroupName(groupName);
    setShowGroupMembersModal(true);
    setLoadingGroupMembers(true);
    
    try {
      const data = await apiClient.get(`/api/groups/details/${groupId}`);
      if (data.success) {
        setSelectedGroupMembers(data.data.members || []);
      }
    } catch (error) {
      console.error('Failed to load group members:', error);
      toast.error('Failed to load group members');
    }
    setLoadingGroupMembers(false);
  };

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 10px' }}>Admin Access Management</h2>
        <p style={{ color: '#666', margin: 0 }}>
          Assign groups as admin groups or add individual users with admin access.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '20px',
        }}
      >
        {/* Left Column: Admin Groups and Users */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Admin Groups */}
          <div>
          <h3 style={{ margin: '0 0 15px', fontSize: '18px', fontWeight: '600' }}>
            Admin Groups ({adminGroups.length})
          </h3>
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              maxHeight: '600px',
              overflowY: 'auto',
            }}
          >
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                Loading admin groups...
              </div>
            ) : adminGroups.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                No admin groups assigned yet. Assign groups from the right column.
              </div>
            ) : (
              adminGroups.map((adminGroup) => (
                <div
                  key={adminGroup.id}
                  style={{
                    padding: '15px 20px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div 
                    style={{ flex: 1, cursor: 'pointer' }}
                    onClick={() => handleViewGroupMembers(adminGroup.groupId, adminGroup.groupName)}
                  >
                    <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                      {adminGroup.groupName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {adminGroup.memberCount} member{adminGroup.memberCount !== 1 ? 's' : ''}
                      {adminGroup.groupDescription && ` • ${adminGroup.groupDescription}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleUnassign(adminGroup.groupId)}
                    style={{
                      padding: '6px 12px',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                    }}
                  >
                    Unassign
                  </button>
                </div>
              ))
            )}
          </div>
          </div>

          {/* Admin Users */}
          <div>
            <h3 style={{ margin: '0 0 15px', fontSize: '18px', fontWeight: '600' }}>
              Admin Users ({adminUsers.length})
            </h3>
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                maxHeight: '600px',
                overflowY: 'auto',
              }}
            >
              {adminUsers.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No individual admin users. Add users from the right column.
                </div>
              ) : (
                adminUsers.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      padding: '15px 20px',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                        {user.displayName}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        {user.email}
                        {user.jobTitle && ` • ${user.jobTitle}`}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const data = await apiClient.post('/api/admins/toggle-user', {
                            userId: user.id,
                            isAdmin: false,
                          });
                          if (data.success) {
                            loadAdminUsers();
                          } else {
                            toast.error(`Failed: ${data.error}`);
                          }
                        } catch (error: any) {
                          toast.error(`Failed: ${error.message}`);
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Add Admin Access Section */}
        <div>
          <h3 style={{ margin: '0 0 15px', fontSize: '18px', fontWeight: '600' }}>
            Add Admin Access
          </h3>
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              padding: '30px',
            }}
          >
            {/* Add Group */}
            <div style={{ marginBottom: '30px', paddingBottom: '30px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '15px' }}>
                Add by Group
              </div>
              <div style={{ marginBottom: '15px', color: '#6b7280', fontSize: '14px' }}>
                Assign entire Microsoft 365 groups. All members will have dashboard access.
              </div>
              <button
                onClick={() => {
                  setShowGroupModal(true);
                  loadM365Groups();
                }}
                style={{
                  padding: '10px 20px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                + Add Group
              </button>
            </div>

            {/* Add User */}
            <div>
              <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '15px' }}>
                Add by User
              </div>
              <div style={{ marginBottom: '15px', color: '#6b7280', fontSize: '14px' }}>
                Grant admin access to individual users without creating a group.
              </div>
              <button
                onClick={openUserModal}
                style={{
                  padding: '10px 20px',
                  background: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                + Add User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowUserModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Manage Individual Admin Access</h3>
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setSelectedUserIds(new Set());
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              Select users to grant or revoke admin access. Click OK when done.
            </p>

            {/* User Search */}
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                borderRadius: '6px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                marginBottom: '15px',
              }}
            />

            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading users...</div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                {userSearch ? 'No users found matching your search.' : 'No users found'}
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#e5e7eb', borderRadius: '8px', overflow: 'hidden', maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                  {filteredUsers.map((user) => {
                    const dbUser = dbUsers.find(u => u.graphUserId === user.graphUserId);
                    const isAdmin = dbUser?.isAdmin || false;
                    const isSelected = selectedUserIds.has(user.graphUserId);
                    return (
                      <div
                        key={user.id}
                        style={{
                          padding: '15px 20px',
                          background: 'white',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          opacity: isAdmin ? 0.6 : 1,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', marginBottom: '3px' }}>
                            {user.displayName}
                            {isAdmin && <span style={{ marginLeft: '8px', color: '#059669', fontSize: '13px', fontWeight: '600' }}>✓ Current Admin</span>}
                          </div>
                          <div style={{ fontSize: '13px', color: '#6b7280' }}>
                            {user.email}
                            {user.jobTitle && ` • ${user.jobTitle}`}
                          </div>
                        </div>
                        {isAdmin ? (
                          <div style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>Already admin</div>
                        ) : (
                          <button
                            onClick={() => toggleUserSelection(user.graphUserId)}
                            style={{
                              padding: '8px 16px',
                              background: isSelected ? '#2563eb' : '#e5e7eb',
                              color: isSelected ? 'white' : '#374151',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              fontWeight: '500',
                            }}
                          >
                            {isSelected ? '✓ Selected' : 'Select'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    onClick={() => {
                      setShowUserModal(false);
                      setSelectedUserIds(new Set());
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#e5e7eb',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={processSelectedUsers}
                    disabled={processingUsers || selectedUserIds.size === 0}
                    style={{
                      padding: '10px 20px',
                      background: processingUsers || selectedUserIds.size === 0 ? '#9ca3af' : '#7c3aed',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: processingUsers || selectedUserIds.size === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {processingUsers ? 'Processing...' : `OK (${selectedUserIds.size})`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowGroupModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Assign Admin Group from Microsoft 365</h3>
              <button
                onClick={() => setShowGroupModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              Select a group to assign as an admin group. All members will automatically have dashboard access.
            </p>

            {/* Search */}
            <input
              type="text"
              placeholder="Search groups..."
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                borderRadius: '6px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                marginBottom: '15px',
              }}
            />

            {loadingM365Groups ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading groups...</div>
            ) : (() => {
              const filteredGroups = groupSearch
                ? m365Groups.filter((g) =>
                    g.displayName?.toLowerCase().includes(groupSearch.toLowerCase())
                  )
                : m365Groups;

              const assignedGroupIds = new Set(adminGroups.map(ag => ag.groupName));

              return filteredGroups.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  {groupSearch ? 'No groups found matching your search.' : 'No groups found'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#e5e7eb', borderRadius: '8px', overflow: 'hidden', maxHeight: '400px', overflowY: 'auto' }}>
                  {filteredGroups.map((group) => {
                    const alreadyAssigned = assignedGroupIds.has(group.displayName);
                    return (
                      <div
                        key={group.id}
                        style={{
                          padding: '15px 20px',
                          background: 'white',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          opacity: alreadyAssigned ? 0.6 : 1,
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', marginBottom: '3px' }}>{group.displayName}</div>
                          <div style={{ fontSize: '13px', color: '#6b7280' }}>
                            {group.description || 'No description'}
                          </div>
                        </div>
                        {alreadyAssigned ? (
                          <div style={{ fontSize: '13px', color: '#6b7280', fontStyle: 'italic' }}>Already assigned</div>
                        ) : (
                          <button
                            onClick={() => assignGroupAsAdmin(group.id, group.displayName)}
                            disabled={assigningGroup === group.id}
                            style={{
                              padding: '8px 16px',
                              background: assigningGroup === group.id ? '#9ca3af' : '#2563eb',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '14px',
                              cursor: assigningGroup === group.id ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {assigningGroup === group.id ? 'Assigning...' : 'Assign'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Group Members Modal */}
      {showGroupMembersModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowGroupMembersModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>{selectedGroupName} - Members</h3>
              <button
                onClick={() => setShowGroupMembersModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              All members of this group have admin access
            </p>

            {loadingGroupMembers ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading members...</div>
            ) : selectedGroupMembers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No members in this group</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#e5e7eb', borderRadius: '8px', overflow: 'hidden', maxHeight: '400px', overflowY: 'auto' }}>
                {selectedGroupMembers.map((member: any) => (
                  <div
                    key={member.id}
                    style={{
                      padding: '15px 20px',
                      background: 'white',
                    }}
                  >
                    <div style={{ fontWeight: '500', marginBottom: '3px' }}>
                      {member.user.displayName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      {member.user.email}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
